import React, { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { type BlockNoteEditor } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { useCreateBlockNote } from '@blocknote/react';
import { useSubjectLanguage } from '@features/user';
import {
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Feedback as FeedbackIcon,
  Message as MessageIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { capitalize } from '@utils/utils';
import { AnimatePresence, motion } from 'motion/react';

import {
  isPepCAction,
  type PepCPayload,
  type RejectionModalContentProps,
  RejectionReason,
} from '../../types';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

type Step = 'initial' | 'edit' | 'confirmation' | 'feedback';

/**
 * PEP-C specific rejection modal content
 */
export const PepCRejection: React.FC<RejectionModalContentProps> = ({
  context,
  onReject,
  onCancel,
  onAccept,
  isLoading = false,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const [countDown, setCountDown] = useState(5);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [isEditorInitialized, setIsEditorInitialized] = useState(false);
  const rejectionTriggered = useRef(false);
  const editorRef = useRef<BlockNoteEditor | null>(null);
  const subjectLanguage = useSubjectLanguage();
  const SubjectLanguage = capitalize(subjectLanguage);

  const isPepC = isPepCAction(context.action);
  const message = (
    isPepC ? context.action.action_payload : { title: '', body: '', reasoning_trace: '' }
  ) as PepCPayload;

  // Initialize edited values
  useEffect(() => {
    setEditedTitle(message.title || '');
    setEditedBody(message.body || '');
  }, [message.title, message.body]);

  // Auto-close countdown for confirmation step
  useEffect(() => {
    if (currentStep === 'confirmation' && countDown > 0) {
      const timer = setTimeout(() => {
        setCountDown(prev => prev - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    } else if (currentStep === 'confirmation' && countDown === 0 && !rejectionTriggered.current) {
      rejectionTriggered.current = true;
      onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
    }
  }, [currentStep, countDown, onReject]);

  // Create the BlockNote editor
  const editor = useCreateBlockNote({
    initialContent: undefined,
  });

  // Store editor reference
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Initialize editor content when entering edit step
  useEffect(() => {
    if (currentStep === 'edit' && !isEditorInitialized) {
      const initializeEditor = async () => {
        try {
          const blocks = await editor.tryParseMarkdownToBlocks(editedBody);
          editor.replaceBlocks(editor.document, blocks);
          setIsEditorInitialized(true);
        } catch (err) {
          console.error('Error initializing editor:', err);
        }
      };
      void initializeEditor();
    }
  }, [currentStep, editor, editedBody, isEditorInitialized]);

  // Handle editor content changes
  const handleEditorChange = useCallback(async () => {
    if (!editorRef.current) return;
    try {
      const markdown = await editorRef.current.blocksToMarkdownLossy(editorRef.current.document);
      setEditedBody(markdown);
    } catch (error) {
      console.error('Error converting to markdown:', error);
    }
  }, []);

  // Type guard to ensure we have a PEP-C action
  if (!isPepC) {
    return null;
  }

  const handleReject = (reason: RejectionReason) => {
    if (reason === RejectionReason.WRONG_SUGGESTION) {
      setCurrentStep('edit');
    } else if (reason === RejectionReason.DONT_CARE_FOR_FEATURE) {
      setCurrentStep('confirmation');
    } else {
      // Must be OTHER
      setCurrentStep('feedback');
    }
  };

  const handleEdit = () => {
    if (onAccept) {
      // Create updated PEP-C payload with edited content
      const updatedPayload: PepCPayload = {
        ...message,
        title: editedTitle,
        body: editedBody,
      };
      // Accept the action with edited content
      onAccept(context.action.action_id, updatedPayload);
    }
  };

  const handleFeedbackSubmit = () => {
    if (feedbackText.trim()) {
      onReject(RejectionReason.OTHER, { feedback: feedbackText });
    }
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setFeedbackText('');
    setCountDown(5);
    rejectionTriggered.current = false;
    setIsEditorInitialized(false);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'initial':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.1 } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {/* Message Summary */}
              <Alert severity="info" icon={<MessageIcon fontSize="inherit" />}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', color: 'primary.dark' }}
                  >
                    Suggested message:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.dark', mt: 0.5 }}>
                    {message.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', mt: 0.5 }}>
                    <Markdown>{`${message.body.substring(0, 100)}...`}</Markdown>
                  </Typography>
                </Box>
              </Alert>

              {/* Rejection Reason Options */}
              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReject(RejectionReason.WRONG_SUGGESTION);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EditIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Inaccurate
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        The message contains incorrect information or doesn&apos;t match the visit
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Don&apos;t show me these anymore
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        I don&apos;t want to use {subjectLanguage} communication features
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReject(RejectionReason.OTHER);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FeedbackIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Other
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Something else prevented me from sending this message
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                Cancel
              </Button>
            </Box>
          </motion.div>
        );

      case 'edit':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.1 } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Edit Message Content
                </Typography>
              </Box>

              <Alert severity="info">
                <Typography variant="body2">
                  Review and modify the message content below. You can then choose to accept the
                  corrected message or reject it entirely.
                </Typography>
              </Alert>

              <Stack spacing={3}>
                <TextField
                  label="Message Title"
                  value={editedTitle}
                  onChange={e => {
                    setEditedTitle(e.target.value);
                  }}
                  fullWidth
                  variant="outlined"
                />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Message Body
                  </Typography>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      minHeight: 200,
                      '& .bn-container': {
                        border: 'none',
                        borderRadius: 0,
                      },
                      '& .bn-editor': {
                        paddingTop: '1rem',
                        paddingBottom: '1rem',
                      },
                    }}
                  >
                    <BlockNoteView
                      editor={editor}
                      theme="light"
                      sideMenu={false}
                      onChange={handleEditorChange}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} disabled={isLoading} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => {
                    onReject(RejectionReason.WRONG_SUGGESTION);
                  }}
                  disabled={isLoading}
                  variant="outlined"
                  color="error"
                >
                  Still Reject
                </Button>
                <Button
                  onClick={handleEdit}
                  disabled={isLoading}
                  variant="contained"
                  color="primary"
                >
                  Accept Changes
                </Button>
              </Box>
            </Box>
          </motion.div>
        );

      case 'confirmation':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.1 } }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                {SubjectLanguage} Communication Suggestions Disabled
              </Typography>

              <Typography variant="body1" color="text.secondary">
                You will no longer receive {subjectLanguage} communication suggestions for future
                notes. You can re-enable this feature anytime in your preferences.
              </Typography>

              {countDown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {countDown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  if (!rejectionTriggered.current) {
                    rejectionTriggered.current = true;
                    onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </motion.div>
        );

      case 'feedback':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.1 } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FeedbackIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Tell us more
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Help us improve by sharing what prevented you from accepting this {subjectLanguage}
                communication suggestion.
              </Typography>

              <TextField
                value={feedbackText}
                onChange={e => {
                  setFeedbackText(e.target.value);
                }}
                multiline
                rows={4}
                fullWidth
                placeholder="Tell us more about why you're not sending this message..."
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} disabled={isLoading} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => {
                    onReject(RejectionReason.OTHER);
                  }}
                  disabled={isLoading}
                  variant="outlined"
                  color="inherit"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleFeedbackSubmit}
                  disabled={isLoading || !feedbackText.trim()}
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return <AnimatePresence mode="popLayout">{renderStep()}</AnimatePresence>;
};
